import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  fetchLeads, fetchBookings, adminLogin, sendDailySummary, getToken, setToken, clearToken,
  fetchPrices, updatePrice, fetchAllOffers, createOffer, patchOffer, deleteOffer,
} from "../lib/api";
import {
  ArrowLeft, RefreshCw, Phone, MessageCircle, Zap, Users, Wrench, Loader2, MapPin,
  Lock, LogOut, Download, Mail, Tag, IndianRupee, Save, Plus, Trash2, Eye, EyeOff, ImagePlus,
} from "lucide-react";

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};
const inr = (n) => (n == null || n === "" ? "—" : "₹ " + Number(n).toLocaleString("en-IN"));

const Th = ({ children }) => <th className="text-left text-[11px] font-bold uppercase tracking-widest text-zinc-500 px-4 py-3 whitespace-nowrap">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 text-sm text-zinc-200 align-top ${className}`}>{children}</td>;

const CallLinks = ({ phone }) => (
  <div className="flex gap-2">
    <a href={`tel:${phone}`} className="flex items-center justify-center w-7 h-7 bg-red-600 hover:bg-red-700 transition-colors" title="Call" data-testid={`db-call-${phone}`}><Phone size={13} className="text-white" /></a>
    <a href={`https://wa.me/91${phone}`} target="_blank" rel="noreferrer" className="flex items-center justify-center w-7 h-7 border border-white/20 hover:border-[#25D366] transition-colors" title="WhatsApp" data-testid={`db-wa-${phone}`}><MessageCircle size={13} className="text-white" /></a>
  </div>
);

const downloadCSV = (name, headers, rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [headers.map((h) => esc(h.label)).join(",")].concat(rows.map((r) => headers.map((h) => esc(h.get(r))).join(","))).join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
};

const field = "w-full bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder:text-zinc-500 px-4 py-3 transition-colors";

function LoginScreen({ onSuccess }) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const { data } = await adminLogin(pw);
      setToken(data.token); onSuccess();
    } catch (e) { setErr(e.response?.data?.detail || "Login failed. Please try again."); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm border border-white/10 bg-[#111] p-8" data-testid="db-login-form">
        <div className="flex items-baseline gap-1.5 justify-center">
          <span className="font-heading text-2xl font-bold uppercase">Sasya</span>
          <span className="font-heading text-2xl font-bold uppercase text-red-600">Hero</span>
        </div>
        <div className="mt-2 flex items-center justify-center gap-2 text-zinc-500 text-xs uppercase tracking-widest"><Lock size={13} /> Team Dashboard</div>
        <input type="password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Staff password" data-testid="db-password-input" className={"mt-7 " + field} />
        {err && <p className="mt-3 text-sm text-red-500" data-testid="db-login-error">{err}</p>}
        <button type="submit" disabled={loading} className="mt-4 w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-bold uppercase tracking-wide py-3 transition-colors" data-testid="db-login-submit">
          {loading ? <Loader2 className="animate-spin" size={18} /> : "Enter Dashboard"}
        </button>
        <Link to="/" className="mt-5 block text-center text-xs text-zinc-500 hover:text-white">← Back to website</Link>
      </form>
    </div>
  );
}

// ---------- Pricing manager ----------
function PricingPanel() {
  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await fetchPrices();
      const order = { Motorcycle: 0, Scooter: 1, "VIDA Electric": 2 };
      data.sort((a, b) => (order[a.category] ?? 9) - (order[b.category] ?? 9));
      setRows(data); setEdits({});
    } catch { toast.error("Could not load prices."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveAll = async () => {
    const changed = Object.entries(edits).filter(([name, val]) => {
      const orig = rows.find((r) => r.name === name)?.price;
      const num = val === "" ? null : Number(val);
      return num !== (orig ?? null);
    });
    if (!changed.length) { toast("No changes to save."); return; }
    setSaving(true);
    try {
      await Promise.all(changed.map(([name, val]) => updatePrice(name, val === "" ? null : Number(val))));
      toast.success(`Updated ${changed.length} price${changed.length > 1 ? "s" : ""}. Live on the website now.`);
      await load();
    } catch (e) { toast.error("Could not save prices."); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-24 text-zinc-500"><Loader2 className="animate-spin mr-2" /> Loading…</div>;

  let lastCat = null;
  return (
    <div className="mt-6" data-testid="db-pricing-panel">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-zinc-400">Edit the on-road starting price for any model. Leave blank for "Price on request".</p>
        <button onClick={saveAll} disabled={saving} className="flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-bold uppercase text-sm px-5 py-2.5 transition-colors" data-testid="db-save-prices">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Changes
        </button>
      </div>
      <div className="border border-white/10 bg-[#111]">
        {rows.map((r) => {
          const showHead = r.category !== lastCat; lastCat = r.category;
          const val = edits[r.name] !== undefined ? edits[r.name] : (r.price ?? "");
          return (
            <div key={r.name}>
              {showHead && <div className="px-4 py-2 bg-white/[0.03] text-xs font-bold uppercase tracking-widest text-red-500 border-b border-white/10">{r.category}</div>}
              <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-white/5">
                <span className="text-sm text-white font-medium">{r.name}</span>
                <div className="flex items-center gap-2">
                  <IndianRupee size={14} className="text-zinc-500" />
                  <input
                    type="number"
                    value={val}
                    onChange={(e) => setEdits({ ...edits, [r.name]: e.target.value })}
                    placeholder="On request"
                    data-testid={`db-price-input-${r.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="w-36 bg-black border border-white/15 focus:border-red-600 outline-none text-white text-right px-3 py-2 text-sm transition-colors"
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Offers manager ----------
function OffersPanel() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", subtitle: "", link: "", image: "", start_date: "", end_date: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await fetchAllOffers(); setOffers(data || []); }
    catch { toast.error("Could not load offers."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) { toast.error("Image too large. Please use an image under 1.5 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image: reader.result }));
    reader.readAsDataURL(file);
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Please enter an offer title."); return; }
    setSaving(true);
    try {
      await createOffer(form);
      toast.success("Offer published — now live on the website.");
      setForm({ title: "", subtitle: "", link: "", image: "", start_date: "", end_date: "" });
      await load();
    } catch { toast.error("Could not save offer."); }
    finally { setSaving(false); }
  };

  const toggle = async (o) => {
    try { await patchOffer(o.offer_id, { active: !o.active }); await load(); }
    catch { toast.error("Could not update offer."); }
  };
  const remove = async (o) => {
    try { await deleteOffer(o.offer_id); toast.success("Offer removed."); await load(); }
    catch { toast.error("Could not delete offer."); }
  };

  return (
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="db-offers-panel">
      <div className="border border-white/10 bg-[#111] p-6">
        <h3 className="font-heading text-xl font-semibold uppercase text-white">Add a new offer</h3>
        <p className="text-sm text-zinc-400 mt-1">Shows in the top banner & offers section for customers.</p>
        <form onSubmit={create} className="mt-5 space-y-4" data-testid="db-offer-form">
          <input className={field} placeholder="Offer title (e.g. Monsoon Dhamaka)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="db-offer-title" />
          <input className={field} placeholder="Short details (e.g. ₹5,000 off + free helmet)" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} data-testid="db-offer-subtitle" />
          <input className={field} placeholder="Link (optional)" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} data-testid="db-offer-link" />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-xs uppercase tracking-widest text-zinc-500">Start date (optional)</span>
              <input type="date" className={field + " mt-1 [color-scheme:dark]"} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="db-offer-start" />
            </div>
            <div>
              <span className="text-xs uppercase tracking-widest text-zinc-500">End date (optional)</span>
              <input type="date" className={field + " mt-1 [color-scheme:dark]"} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="db-offer-end" />
            </div>
          </div>
          <p className="text-xs text-zinc-500">Leave dates blank to show immediately with no expiry. Offers auto-appear on the start date and disappear after the end date.</p>
          <label className="flex items-center gap-3 border border-dashed border-white/20 hover:border-red-600 px-4 py-3 cursor-pointer transition-colors" data-testid="db-offer-image-label">
            <ImagePlus size={18} className="text-red-500" />
            <span className="text-sm text-zinc-300">{form.image ? "Image selected — change" : "Upload banner image (optional, < 1.5 MB)"}</span>
            <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="db-offer-image-input" />
          </label>
          {form.image && <img src={form.image} alt="preview" className="w-full max-h-40 object-cover border border-white/10" />}
          <button type="submit" disabled={saving} className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-bold uppercase tracking-wide py-3 transition-colors" data-testid="db-offer-submit">
            {saving ? <Loader2 className="animate-spin" size={18} /> : <><Plus size={16} /> Publish Offer</>}
          </button>
        </form>
      </div>

      <div className="border border-white/10 bg-[#111] p-6">
        <h3 className="font-heading text-xl font-semibold uppercase text-white">Current offers</h3>
        {loading ? (
          <div className="flex items-center justify-center py-16 text-zinc-500"><Loader2 className="animate-spin mr-2" /> Loading…</div>
        ) : offers.length === 0 ? (
          <p className="py-10 text-center text-zinc-500 text-sm">No offers yet. Add one on the left.</p>
        ) : (
          <div className="mt-4 space-y-3">
            {offers.map((o) => (
              <div key={o.offer_id} className="flex items-center gap-3 border border-white/10 p-3" data-testid={`db-offer-row-${o.offer_id}`}>
                {o.image ? <img src={o.image} alt="" className="w-14 h-14 object-cover shrink-0" /> : <div className="w-14 h-14 bg-white/5 flex items-center justify-center shrink-0"><Tag size={18} className="text-zinc-500" /></div>}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold truncate">{o.title}</p>
                  {o.subtitle && <p className="text-zinc-400 text-xs truncate">{o.subtitle}</p>}
                  {(o.start_date || o.end_date) && (
                    <p className="text-zinc-500 text-[11px] mt-0.5">{o.start_date || "now"} → {o.end_date || "no expiry"}</p>
                  )}
                  {(() => {
                    const today = new Date().toISOString().split("T")[0];
                    let s = { t: "Live", c: "bg-green-600/20 text-green-400" };
                    if (!o.active) s = { t: "Hidden", c: "bg-zinc-600/30 text-zinc-400" };
                    else if (o.start_date && o.start_date > today) s = { t: "Scheduled", c: "bg-amber-600/20 text-amber-400" };
                    else if (o.end_date && o.end_date < today) s = { t: "Expired", c: "bg-red-600/20 text-red-400" };
                    return <span className={`inline-block mt-1 text-[10px] font-bold uppercase px-2 py-0.5 ${s.c}`}>{s.t}</span>;
                  })()}
                </div>
                <button onClick={() => toggle(o)} className="p-2 border border-white/15 hover:border-white transition-colors" title={o.active ? "Hide" : "Show"} data-testid={`db-offer-toggle-${o.offer_id}`}>
                  {o.active ? <EyeOff size={15} className="text-white" /> : <Eye size={15} className="text-white" />}
                </button>
                <button onClick={() => remove(o)} className="p-2 bg-red-600 hover:bg-red-700 transition-colors" title="Delete" data-testid={`db-offer-delete-${o.offer_id}`}>
                  <Trash2 size={15} className="text-white" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Dashboard ----------
export default function Dashboard() {
  const [authed, setAuthed] = useState(!!getToken());
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const logout = useCallback(() => { clearToken(); setAuthed(false); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, b] = await Promise.all([fetchLeads(), fetchBookings()]);
      setLeads(l.data || []); setBookings(b.data || []);
    } catch (e) {
      if (e.response?.status === 401) { toast.error("Session expired. Please log in again."); logout(); }
      else toast.error("Could not load data.");
    } finally { setLoading(false); }
  }, [logout]);

  useEffect(() => { if (authed) load(); }, [authed, load]);

  const emailSummary = async () => {
    setSending(true);
    try { await sendDailySummary(); toast.success("Daily summary emailed to the showroom inbox."); }
    catch (e) { if (e.response?.status === 401) logout(); else toast.error("Could not send summary."); }
    finally { setSending(false); }
  };

  const exportLeads = () => downloadCSV("sasya-sales-enquiries.csv", [
    { label: "Received", get: (r) => fmt(r.created_at) }, { label: "Name", get: (r) => r.name },
    { label: "Mobile", get: (r) => r.mobile }, { label: "Category", get: (r) => r.category },
    { label: "Model", get: (r) => r.vehicle }, { label: "Test Ride", get: (r) => (r.test_drive ? "Yes" : "No") },
    { label: "Pincode", get: (r) => r.pincode }, { label: "Message", get: (r) => r.message },
  ], leads);

  const exportBookings = () => downloadCSV("sasya-service-bookings.csv", [
    { label: "Received", get: (r) => fmt(r.created_at) }, { label: "Name", get: (r) => r.name },
    { label: "Phone", get: (r) => r.mobile }, { label: "Vehicle Reg No", get: (r) => r.vehicle_reg_no },
    { label: "Service Date", get: (r) => r.booking_date }, { label: "Pincode", get: (r) => r.pincode },
  ], bookings);

  if (!authed) return <LoginScreen onSuccess={() => setAuthed(true)} />;

  const testRides = leads.filter((l) => l.test_drive).length;
  const stats = [
    { icon: Users, label: "Sales Enquiries", value: leads.length },
    { icon: Zap, label: "Test Ride Requests", value: testRides },
    { icon: Wrench, label: "Service Bookings", value: bookings.length },
  ];
  const TABS = [
    { id: "leads", label: "Sales Enquiries" }, { id: "service", label: "Service Bookings" },
    { id: "pricing", label: "Pricing" }, { id: "offers", label: "Offers" },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <header className="sticky top-0 z-20 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-4 min-w-0">
            <Link to="/" className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm shrink-0" data-testid="db-back"><ArrowLeft size={16} /> <span className="hidden sm:inline">Site</span></Link>
            <span className="h-5 w-px bg-white/15" />
            <div className="flex items-baseline gap-1.5">
              <span className="font-heading text-lg font-bold uppercase">Sasya</span>
              <span className="font-heading text-lg font-bold uppercase text-red-600">Hero</span>
              <span className="text-zinc-500 text-sm ml-2 hidden md:inline">Dashboard</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={emailSummary} disabled={sending} className="flex items-center gap-2 border border-white/15 hover:border-white px-3 md:px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-60" data-testid="db-email-summary" title="Email today's summary now">
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />} <span className="hidden sm:inline">Email Summary</span>
            </button>
            <button onClick={logout} className="flex items-center gap-2 bg-red-600 hover:bg-red-700 px-3 md:px-4 py-2 text-sm font-semibold transition-colors" data-testid="db-logout"><LogOut size={15} /> <span className="hidden sm:inline">Logout</span></button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 md:px-10 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {stats.map((s) => (
            <div key={s.label} className="border border-white/10 bg-[#111] p-6" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-widest text-zinc-500">{s.label}</span><s.icon className="text-red-600" size={18} /></div>
              <div className="mt-3 font-heading text-4xl font-bold">{s.value}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-3 border-b border-white/10">
          <div className="flex gap-1 flex-wrap">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`db-tab-${t.id}`}
                className={`px-4 md:px-5 py-3 font-heading text-sm md:text-base font-semibold uppercase tracking-wide border-b-2 -mb-px transition-colors ${tab === t.id ? "border-red-600 text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
                {t.label}
              </button>
            ))}
          </div>
          {(tab === "leads" || tab === "service") && (
            <div className="flex items-center gap-4 mb-2">
              <button onClick={load} className="flex items-center gap-2 text-sm font-semibold text-zinc-300 hover:text-white" data-testid="db-refresh"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh</button>
              <button onClick={tab === "leads" ? exportLeads : exportBookings} className="flex items-center gap-2 text-sm font-semibold text-zinc-300 hover:text-white" data-testid="db-export-csv"><Download size={15} /> Export CSV</button>
            </div>
          )}
        </div>

        {tab === "pricing" ? <PricingPanel /> : tab === "offers" ? <OffersPanel /> : loading ? (
          <div className="flex items-center justify-center py-24 text-zinc-500"><Loader2 className="animate-spin mr-2" /> Loading…</div>
        ) : tab === "leads" ? (
          <div className="mt-6 border border-white/10 bg-[#111] overflow-x-auto" data-testid="db-leads-table">
            {leads.length === 0 ? <p className="p-10 text-center text-zinc-500">No sales enquiries yet.</p> : (
              <table className="w-full border-collapse">
                <thead className="border-b border-white/10"><tr><Th>Received</Th><Th>Name</Th><Th>Mobile</Th><Th>Category</Th><Th>Model</Th><Th>Test Ride</Th><Th>Pincode</Th><Th>Message</Th><Th>Action</Th></tr></thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.lead_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <Td className="text-zinc-400 whitespace-nowrap">{fmt(l.created_at)}</Td>
                      <Td className="font-semibold">{l.name}</Td><Td>{l.mobile}</Td><Td>{l.category}</Td><Td>{l.vehicle || "—"}</Td>
                      <Td>{l.test_drive ? <span className="inline-flex items-center gap-1 bg-red-600 text-white text-[10px] font-bold uppercase px-2 py-0.5"><Zap size={11} /> Yes</span> : <span className="text-zinc-500">No</span>}</Td>
                      <Td>{l.pincode || "—"}</Td><Td className="max-w-[220px] text-zinc-400">{l.message || "—"}</Td>
                      <Td><CallLinks phone={l.mobile} /></Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          <div className="mt-6 border border-white/10 bg-[#111] overflow-x-auto" data-testid="db-service-table">
            {bookings.length === 0 ? <p className="p-10 text-center text-zinc-500">No service bookings yet.</p> : (
              <table className="w-full border-collapse">
                <thead className="border-b border-white/10"><tr><Th>Received</Th><Th>Name</Th><Th>Phone</Th><Th>Vehicle Reg. No</Th><Th>Service Date</Th><Th>Pincode</Th><Th>Action</Th></tr></thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.booking_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <Td className="text-zinc-400 whitespace-nowrap">{fmt(b.created_at)}</Td>
                      <Td className="font-semibold">{b.name}</Td><Td>{b.mobile}</Td><Td className="uppercase">{b.vehicle_reg_no || "—"}</Td>
                      <Td className="whitespace-nowrap">{b.booking_date}</Td>
                      <Td><span className="inline-flex items-center gap-1">{b.pincode ? <><MapPin size={12} className="text-red-500" />{b.pincode}</> : "—"}</span></Td>
                      <Td><CallLinks phone={b.mobile} /></Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
